import { type CountryCode } from "libphonenumber-js";

/**
 * Obfuscate a phone number with asteriks.
 *
 * @param phone String
 * @returns String
 *
 * obfuscatePhone("+11234567890")
 * => ***-***-7890
 */
function obfuscatePhone(phone: string): string {
  const lastFour = phone.slice(-4);
  return `***-***-${lastFour}`;
}

const SUPPORTED_COUNTRIES = [
  { name: "Afghanistan", iso: "AF", code: "(+93)" },
  { name: "Aland Islands", iso: "AX", code: "35818" },
  { name: "Albania", iso: "AL", code: "(+355)" },
  { name: "Algeria", iso: "DZ", code: "(+213)" },
  { name: "American Samoa", iso: "AS", code: "(+1684)" },
  { name: "Andorra", iso: "AD", code: "(+376)" },
  { name: "Angola", iso: "AO", code: "(+244)" },
  { name: "Anguilla", iso: "AI", code: "(+1264)" },
  { name: "Antigua and Barbuda", iso: "AG", code: "(+1268)" },
  { name: "Argentina", iso: "AR", code: "(+54)" },
  { name: "Armenia", iso: "AM", code: "(+374)" },
  { name: "Aruba", iso: "AW", code: "(+297)" },
  { name: "Australia", iso: "AU", code: "(+61)" },
  { name: "Austria", iso: "AT", code: "(+43)" },
  { name: "Azerbaijan", iso: "AZ", code: "(+994)" },
  { name: "Bahamas", iso: "BS", code: "(+1242)" },
  { name: "Bahrain", iso: "BH", code: "(+973)" },
  { name: "Bangladesh", iso: "BD", code: "(+880)" },
  { name: "Barbados", iso: "BB", code: "(+1246)" },
  { name: "Belarus", iso: "BY", code: "(+375)" },
  { name: "Belgium", iso: "BE", code: "(+32)" },
  { name: "Belize", iso: "BZ", code: "(+501)" },
  { name: "Benin", iso: "BJ", code: "(+229)" },
  { name: "Bermuda", iso: "BM", code: "(+1441)" },
  { name: "Bhutan", iso: "BT", code: "(+975)" },
  { name: "Bolivia", iso: "BO", code: "(+591)" },
  { name: "Bonaire, Sint Eustatius And Saba", iso: "BQ", code: "5993" },
  { name: "Bosnia and Herzegovina", iso: "BA", code: "(+387)" },
  { name: "Botswana", iso: "BW", code: "(+267)" },
  { name: "Brazil", iso: "BR", code: "(+55)" },
  { name: "British Indian Ocean Territory", iso: "IO", code: "(+246)" },
  { name: "Brunei Darussalam", iso: "BN", code: "(+673)" },
  { name: "Bulgaria", iso: "BG", code: "(+359)" },
  { name: "Burkina Faso", iso: "BF", code: "(+226)" },
  { name: "Burundi", iso: "BI", code: "(+257)" },
  { name: "Cambodia", iso: "KH", code: "(+855)" },
  { name: "Cameroon", iso: "CM", code: "(+237)" },
  { name: "Cape Verde", iso: "CV", code: "238" },
  { name: "Canada", iso: "CA", code: "(+1)" },
  { name: "Cayman Islands", iso: "KY", code: "(+1345)" },
  { name: "Central African Republic", iso: "CF", code: "(+236)" },
  { name: "Chad", iso: "TD", code: "(+235)" },
  { name: "Chile", iso: "CL", code: "(+56)" },
  { name: "China", iso: "CN", code: "(+86)" },
  { name: "Christmas Island", iso: "CX", code: "(+6189164)" },
  { name: "Cocos (Keeling) Islands", iso: "CC", code: "(+672)" },
  { name: "Colombia", iso: "CO", code: "(+57)" },
  { name: "Comoros", iso: "KM", code: "(+269)" },
  { name: "Congo", iso: "CG", code: "(+242)" },
  { name: "Cook Islands", iso: "CK", code: "(+682)" },
  { name: "Costa Rica", iso: "CR", code: "(+506)" },
  { name: "Cote d`Ivoire", iso: "CI", code: "225" },
  { name: "Croatia", iso: "HR", code: "(+385)" },
  { name: "Cuba", iso: "CU", code: "(+53)" },
  { name: "Curacao", iso: "CW", code: "5999" },
  { name: "Cyprus", iso: "CY", code: "(+357)" },
  { name: "Czechia", iso: "CZ", code: "(+420)" },
  { name: "Democratic People`s Republic of Korea", iso: "KP", code: "850" },
  { name: "Democratic Republic of the Congo", iso: "CD", code: "243" },
  { name: "Denmark", iso: "DK", code: "(+45)" },
  { name: "Djibouti", iso: "DJ", code: "(+253)" },
  { name: "Dominica", iso: "DM", code: "(+1767)" },
  { name: "Dominican Republic", iso: "DO", code: "(+1809)" },
  { name: "Ecuador", iso: "EC", code: "(+593)" },
  { name: "Egypt", iso: "EG", code: "(+20)" },
  { name: "El Salvador", iso: "SV", code: "(+503)" },
  { name: "Equatorial Guinea", iso: "GQ", code: "(+240)" },
  { name: "Eritrea", iso: "ER", code: "(+291)" },
  { name: "Estonia", iso: "EE", code: "(+372)" },
  { name: "Ethiopia", iso: "ET", code: "(+251)" },
  { name: "Falkland Islands (Malvinas)", iso: "FK", code: "(+500)" },
  { name: "Faroe Islands", iso: "FO", code: "(+298)" },
  { name: "Fiji", iso: "FJ", code: "(+679)" },
  { name: "Finland", iso: "FI", code: "(+358)" },
  { name: "France", iso: "FR", code: "(+33)" },
  { name: "French Guiana", iso: "GF", code: "(+594)" },
  { name: "French Polynesia", iso: "PF", code: "(+689)" },
  { name: "Gabon", iso: "GA", code: "(+241)" },
  { name: "Gambia", iso: "GM", code: "(+220)" },
  { name: "Georgia", iso: "GE", code: "(+995)" },
  { name: "Germany", iso: "DE", code: "(+49)" },
  { name: "Ghana", iso: "GH", code: "(+233)" },
  { name: "Gibraltar", iso: "GI", code: "(+350)" },
  { name: "Greece", iso: "GR", code: "(+30)" },
  { name: "Greenland", iso: "GL", code: "(+299)" },
  { name: "Grenada", iso: "GD", code: "(+1473)" },
  { name: "Guadeloupe", iso: "GP", code: "(+590)" },
  { name: "Guam", iso: "GU", code: "(+1671)" },
  { name: "Guatemala", iso: "GT", code: "(+502)" },
  { name: "Guernsey", iso: "GG", code: "(+441481)" },
  { name: "Guinea", iso: "GN", code: "(+224)" },
  { name: "Guinea-Bissau", iso: "GW", code: "(+245)" },
  { name: "Guyana", iso: "GY", code: "(+592)" },
  { name: "Haiti", iso: "HT", code: "(+509)" },
  { name: "Holy See (Vatican City State)", iso: "VA", code: "(+3906698)" },
  { name: "Honduras", iso: "HN", code: "(+504)" },
  {
    name: "Hong Kong (Special Administrative Region of China)",
    iso: "HK",
    code: "852",
  },
  { name: "Hungary", iso: "HU", code: "(+36)" },
  { name: "Iceland", iso: "IS", code: "(+354)" },
  { name: "India", iso: "IN", code: "(+91)" },
  { name: "Indonesia", iso: "ID", code: "(+62)" },
  { name: "Iran (Islamic Republic of)", iso: "IR", code: "98" },
  { name: "Iraq", iso: "IQ", code: "(+964)" },
  { name: "Ireland", iso: "IE", code: "(+353)" },
  { name: "Isle Of Man", iso: "IM", code: "441624" },
  { name: "Israel", iso: "IL", code: "(+972)" },
  { name: "Italy", iso: "IT", code: "(+39)" },
  { name: "Jamaica", iso: "JM", code: "(+1876)" },
  { name: "Japan", iso: "JP", code: "(+81)" },
  { name: "Jersey", iso: "JE", code: "(+441534)" },
  { name: "Jordan", iso: "JO", code: "(+962)" },
  { name: "Kazakhstan", iso: "KZ", code: "(+7)" },
  { name: "Kenya", iso: "KE", code: "(+254)" },
  { name: "Kiribati", iso: "KI", code: "(+686)" },
  { name: "Kosovo", iso: "XK", code: "383" },
  { name: "Kuwait", iso: "KW", code: "(+965)" },
  { name: "Kyrgyzstan", iso: "KG", code: "(+996)" },
  { name: "Lao People`s Democratic Republic", iso: "LA", code: "856" },
  { name: "Latvia", iso: "LV", code: "(+371)" },
  { name: "Lebanon", iso: "LB", code: "(+961)" },
  { name: "Lesotho", iso: "LS", code: "(+266)" },
  { name: "Liberia", iso: "LR", code: "(+231)" },
  { name: "Libya", iso: "LY", code: "218" },
  { name: "Liechtenstein", iso: "LI", code: "(+423)" },
  { name: "Lithuania", iso: "LT", code: "(+370)" },
  { name: "Luxembourg", iso: "LU", code: "(+352)" },
  {
    name: "Macau (Special Administrative Region of China)",
    iso: "MO",
    code: "853",
  },
  { name: "Madagascar", iso: "MG", code: "(+261)" },
  { name: "Malawi", iso: "MW", code: "(+265)" },
  { name: "Malaysia", iso: "MY", code: "(+60)" },
  { name: "Maldives", iso: "MV", code: "(+960)" },
  { name: "Mali", iso: "ML", code: "(+223)" },
  { name: "Malta", iso: "MT", code: "(+356)" },
  { name: "Marshall Islands", iso: "MH", code: "(+692)" },
  { name: "Martinique", iso: "MQ", code: "(+596)" },
  { name: "Mauritania", iso: "MR", code: "(+222)" },
  { name: "Mauritius", iso: "MU", code: "(+230)" },
  { name: "Mayotte", iso: "YT", code: "(+262269)" },
  { name: "Mexico", iso: "MX", code: "(+52)" },
  { name: "Micronesia (Federated States of)", iso: "FM", code: "691" },
  { name: "Moldova (Republic of)", iso: "MD", code: "373" },
  { name: "Monaco", iso: "MC", code: "(+377)" },
  { name: "Mongolia", iso: "MN", code: "(+976)" },
  { name: "Montenegro", iso: "ME", code: "(+382)" },
  { name: "Montserrat", iso: "MS", code: "(+1664)" },
  { name: "Morocco", iso: "MA", code: "(+212)" },
  { name: "Mozambique", iso: "MZ", code: "(+258)" },
  { name: "Myanmar", iso: "MM", code: "(+95)" },
  { name: "Namibia", iso: "NA", code: "(+264)" },
  { name: "Nauru", iso: "NR", code: "(+674)" },
  { name: "Nepal", iso: "NP", code: "(+977)" },
  { name: "Netherlands", iso: "NL", code: "(+31)" },
  { name: "New Caledonia", iso: "NC", code: "(+687)" },
  { name: "New Zealand", iso: "NZ", code: "(+64)" },
  { name: "Nicaragua", iso: "NI", code: "(+505)" },
  { name: "Niger", iso: "NE", code: "(+227)" },
  { name: "Nigeria", iso: "NG", code: "(+234)" },
  { name: "Niue", iso: "NU", code: "(+683)" },
  { name: "Norfolk Island", iso: "NF", code: "(+672)" },
  {
    name: "North Macedonia (Republic of North Macedonia)",
    iso: "MK",
    code: "389",
  },
  { name: "Northern Mariana Islands", iso: "MP", code: "(+1670)" },
  { name: "Norway", iso: "NO", code: "(+47)" },
  { name: "Oman", iso: "OM", code: "(+968)" },
  { name: "Pakistan", iso: "PK", code: "(+92)" },
  { name: "Palau", iso: "PW", code: "(+680)" },
  { name: "Panama", iso: "PA", code: "(+507)" },
  { name: "Papua New Guinea", iso: "PG", code: "(+675)" },
  { name: "Paraguay", iso: "PY", code: "(+595)" },
  { name: "Peru", iso: "PE", code: "(+51)" },
  { name: "Philippines", iso: "PH", code: "(+63)" },
  // { name: 'Pitcairn', iso: 'PN', code: '(+64)' },
  { name: "Poland", iso: "PL", code: "(+48)" },
  { name: "Portugal", iso: "PT", code: "(+351)" },
  { name: "Puerto Rico", iso: "PR", code: "(+1787)" },
  { name: "Qatar", iso: "QA", code: "(+974)" },
  { name: "Republic of Korea", iso: "KR", code: "82" },
  { name: "Reunion", iso: "RE", code: "262" },
  { name: "Romania", iso: "RO", code: "(+40)" },
  { name: "Russian Federation", iso: "RU", code: "(+7)" },
  { name: "Rwanda", iso: "RW", code: "(+250)" },
  { name: "Saint Barthelemy", iso: "BL", code: "590" },
  { name: "Saint Helena", iso: "SH", code: "290" },
  { name: "Saint Kitts and Nevis", iso: "KN", code: "(+1869)" },
  { name: "Saint Lucia", iso: "LC", code: "(+1758)" },
  { name: "Saint Martin French", iso: "MF", code: "590" },
  { name: "Saint Pierre and Miquelon", iso: "PM", code: "(+508)" },
  { name: "Saint Vincent and the Grenadines", iso: "VC", code: "(+1784)" },
  { name: "Samoa", iso: "WS", code: "(+685)" },
  { name: "San Marino", iso: "SM", code: "(+378)" },
  { name: "Sao Tome and Principe", iso: "ST", code: "(+239)" },
  { name: "Saudi Arabia", iso: "SA", code: "(+966)" },
  { name: "Senegal", iso: "SN", code: "(+221)" },
  { name: "Serbia", iso: "RS", code: "(+381)" },
  { name: "Seychelles", iso: "SC", code: "(+248)" },
  { name: "Sierra Leone", iso: "SL", code: "(+232)" },
  { name: "Singapore", iso: "SG", code: "(+65)" },
  { name: "Sint Maarten Dutch", iso: "SX", code: "1721" },
  { name: "Slovakia", iso: "SK", code: "(+421)" },
  { name: "Slovenia", iso: "SI", code: "(+386)" },
  { name: "Solomon Islands", iso: "SB", code: "(+677)" },
  { name: "Somalia", iso: "SO", code: "(+252)" },
  { name: "South Africa", iso: "ZA", code: "(+27)" },
  { name: "South Sudan", iso: "SS", code: "(+211)" },
  { name: "Spain", iso: "ES", code: "(+34)" },
  { name: "Sri Lanka", iso: "LK", code: "(+94)" },
  { name: "Sudan", iso: "SD", code: "(+249)" },
  { name: "Suriname", iso: "SR", code: "(+597)" },
  { name: "Svalbard and Jan Mayen Islands", iso: "SJ", code: "4779" },
  { name: "Swaziland", iso: "SZ", code: "268" },
  { name: "Sweden", iso: "SE", code: "(+46)" },
  { name: "Switzerland", iso: "CH", code: "(+41)" },
  { name: "Syrian Arab Republic", iso: "SY", code: "(+963)" },
  { name: "Taiwan (Province of China)", iso: "TW", code: "886" },
  { name: "Tajikistan", iso: "TJ", code: "(+992)" },
  { name: "Tanzania (United Republic of)", iso: "TZ", code: "255" },
  { name: "Thailand", iso: "TH", code: "(+66)" },
  { name: "Timor-Leste (East Timor)", iso: "TL", code: "670" },
  { name: "Togo", iso: "TG", code: "(+228)" },
  { name: "Tokelau", iso: "TK", code: "(+690)" },
  { name: "Tonga", iso: "TO", code: "(+676)" },
  { name: "Trinidad and Tobago", iso: "TT", code: "(+1868)" },
  { name: "Tunisia", iso: "TN", code: "(+216)" },
  { name: "Turkey", iso: "TR", code: "90" },
  { name: "Turkmenistan", iso: "TM", code: "(+993)" },
  { name: "Turks and Caicos Islands", iso: "TC", code: "(+1649)" },
  { name: "Tuvalu", iso: "TV", code: "(+688)" },
  { name: "Uganda", iso: "UG", code: "(+256)" },
  { name: "Ukraine", iso: "UA", code: "(+380)" },
  { name: "United Arab Emirates", iso: "AE", code: "(+971)" },
  { name: "United Kingdom", iso: "GB", code: "(+44)" },
  { name: "United States", iso: "US", code: "(+1)" },
  // { name: 'United States Minor Outlying Islands', iso: 'UM', code: '(+1)' },
  { name: "Uruguay", iso: "UY", code: "(+598)" },
  { name: "Uzbekistan", iso: "UZ", code: "(+998)" },
  { name: "Vanuatu", iso: "VU", code: "(+678)" },
  { name: "Venezuela", iso: "VE", code: "(+58)" },
  { name: "Vietnam", iso: "VN", code: "(+84)" },
  { name: "Virgin Islands British", iso: "VG", code: "1284" },
  { name: "Virgin Islands US", iso: "VI", code: "1340" },
  { name: "Wallis and Futuna Islands", iso: "WF", code: "681" },
  { name: "Western Sahara", iso: "EH", code: "(+212)" },
  { name: "Yemen", iso: "YE", code: "(+967)" },
  { name: "Zambia", iso: "ZM", code: "(+260)" },
  { name: "Zimbabwe", iso: "ZW", code: "(+263)" },
];

const SUPPORTED_ISO_CODES: CountryCode[] = SUPPORTED_COUNTRIES.map(
  (country) => country.iso as CountryCode,
);

export { SUPPORTED_ISO_CODES, type CountryCode, obfuscatePhone };
